import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTableBooking } from '@/features/booking/hooks/useTableBooking';
import { useLoadingContext } from '@/hooks';
import { 
  HeroSection, 
  CalendarView, 
  BookingSummary, 
  TableLayout, 
  VenueInfo, 
  SlotSelectorModal 
} from '@/features/booking/components';
import { CalendarIcon } from '@/components/icons';
import { formatTime, formatCurrencyForDisplay } from '@/lib/utils';
import type { TableAvailabilitySlot } from '@/features/booking/types/tableBookingTypes';

const TableBookingPage = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoadingContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tableForModal, setTableForModal] = useState<string | null>(null);

  const {
    selectedEventSchedule,
    tableAvailability,
    selectedDate,
    selectedBookings,
    allTablesInVenue,
    bookingCategories,
    availableDates,
    totalPrice,
    loading,
    error,
    selectDate,
    addBooking,
    removeBooking,
    createBooking,
  } = useTableBooking(eventId || '');


  const timezone = selectedEventSchedule?.timezone_id || 'UTC';
  const formatPrice = (amount: number, currency: string = 'USD') => formatCurrencyForDisplay(amount, currency);

  // Set initial date when available dates change
  useEffect(() => {
    if (availableDates.length > 0 && !selectedDate) {
      selectDate(availableDates[0]);
    }
  }, [availableDates, selectedDate, selectDate]);

  // Trigger loading when data is loading
  useEffect(() => {
    if (loading) {
      startLoading('table-booking-data');
    } else {
      stopLoading('table-booking-data');
    }
    
    // Cleanup on unmount
    return () => {
      stopLoading('table-booking-data');
    };
  }, [loading, startLoading, stopLoading]);

  const handleDateChange = (date: string) => {
    selectDate(date);
  };

  const handleTableClick = (tableNumber: string) => {
    if (tableAvailability[tableNumber]) {
      setTableForModal(tableNumber);
      setIsModalOpen(true);
    }
  };
  
  const handleSlotSelection = (slot: TableAvailabilitySlot) => {
    if (tableForModal) {
      addBooking(tableForModal, slot);
    }
    setIsModalOpen(false);
    setTableForModal(null);
  };
  
  const handleRemoveBooking = (tableNumber: string) => {
    removeBooking(tableNumber);
  };

  const handleBooking = async () => {
    if (Object.keys(selectedBookings).length === 0) {
      alert("Please select at least one table to book.");
      return;
    }

    try {
      await createBooking();

      const bookingDetails = Object.entries(selectedBookings).map(([table, slot]) => 
        ` - Table ${table} at ${formatTime(slot.start_time, timezone)} for ${formatPrice(slot.event_booking_category.price, slot.event_booking_category.currency)}`
      ).join('\n');

      alert(`Redirecting to confirm booking for:\n\n${bookingDetails}\n\nTotal: ${formatPrice(totalPrice)}`);
      // TODO: Navigate to confirmation page
    } catch (error) {
      alert(`Booking failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (loading) {
    return <div className="bg-black min-h-screen">
      {/* Loading is handled by the global loader */}
    </div>;
  }

  if (error) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Error Loading Event</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <button 
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!selectedEventSchedule) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">No Event Found</h2>
          <p className="text-gray-400 mb-4">The requested event could not be found.</p>
          <button 
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const business = selectedEventSchedule.event.business;
  const event = selectedEventSchedule.event;
  const selectedTableForModalDetails = allTablesInVenue.find(t => t.number === tableForModal);

  return (
    <>
    <div className="bg-black min-h-screen text-white font-sans">
      {/* Hero Section */}
      <div className="relative">
        <HeroSection event={event} business={business} />
        
        {/* Floating Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-6 left-6 w-10 h-10 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:text-gray-200 transition-all duration-200 group z-10"
        >
          <svg 
            className="w-5 h-5 transform group-hover:-translate-x-0.5 transition-transform duration-200" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* Booking Categories */}
      <div className="bg-black border-y border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="text-sm font-semibold text-gray-400 mr-2">Available Tiers:</span>
            {Array.from(bookingCategories).map(category => (
              <div key={category} className="px-3 py-1 bg-gray-800/50 border border-gray-700 rounded-full text-xs font-medium">
                {category}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          
          {/* Left Panel - Calendar & Booking Summary */}
          <div className="lg:col-span-1 space-y-8">
            {/* 1. Calendar */}
            <div>
              <h2 className="flex items-center text-xl font-semibold mb-4">
                <CalendarIcon />
                <span className="ml-3">1. Select a Date</span>
              </h2>
              <CalendarView 
                availableDates={availableDates}
                selectedDate={selectedDate}
                onDateSelect={handleDateChange}
                businessTimeZone={timezone}
              />
            </div>

            {/* 2. Booking Summary */}
            <BookingSummary
              selectedBookings={selectedBookings}
              totalPrice={totalPrice}
              onRemoveBooking={handleRemoveBooking}
              onBooking={handleBooking}
              businessTimeZone={timezone}
            />
          </div>
          
          {/* Right Panel - Table Layout */}
          <div className="lg:col-span-2">
            <TableLayout
              allTablesInVenue={allTablesInVenue}
              tableAvailability={tableAvailability}
              selectedBookings={selectedBookings}
              onTableClick={handleTableClick}
              selectedDate={selectedDate}
              businessTimeZone={timezone}
            />
            
            {/* Venue Info */}
            <VenueInfo 
              business={business}
              eventDescription={event.description}
            />
          </div>
        </div>
      </main>

      <SlotSelectorModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        tableNumber={tableForModal || ''}
        tableSize={selectedTableForModalDetails?.size || 0}
        slots={tableForModal ? tableAvailability[tableForModal] || [] : []}
        onSlotSelect={handleSlotSelection}
        businessTimeZone={timezone}
        eventStartTime={selectedEventSchedule.start_time}
      />
    </div>
    </>
  );
};

export default TableBookingPage;
